import { useState } from "react";
import { ArrowRight, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Overline } from "../components/Overline";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { api } from "../lib/api";

const PAYMENT_LINKS = {
  GBP: "https://pay.gocardless.com/BRT01M57CERWJWC8KWB0Z2H13Q9X9",
  USD: "https://pay.gocardless.com/BRT01M57CJ3VKXE55MW9SNWWS05QB",
  EUR: "https://pay.gocardless.com/BRT01M57S07WYZ99XT17ET9QCDRBH",
};

const PRICES = {
  USD: { symbol: "$", amount: "3,000", label: "USD", payHref: PAYMENT_LINKS.USD },
  GBP: { symbol: "£", amount: "2,400", label: "GBP", payHref: PAYMENT_LINKS.GBP },
  EUR: { symbol: "€", amount: "2,500", label: "EUR", payHref: PAYMENT_LINKS.EUR },
};

const CALENDLY_BOOKING_URL = "https://calendly.com/aimawomenshealth/freecall?month=2026-10";

const detectCurrency = () => {
  try {
    const locale = navigator.language || "";
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (locale.includes("US") || tz.startsWith("America")) return "USD";
    if (tz === "Europe/London" || locale.includes("GB")) return "GBP";
    if (tz.startsWith("Europe")) return "EUR";
  } catch {}
  return "GBP";
};

const scrollToApply = () => {
  document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
};

const scrollToInvestment = () => {
  document.getElementById("investment")?.scrollIntoView({ behavior: "smooth" });
};

const ApplyButton = ({ testId, label = "Apply for the Hormone Health Concierge", light = false }) => (
  <Button
    data-testid={testId}
    onClick={scrollToApply}
    className={`${
      light
        ? "bg-white text-[#5fa292] hover:bg-[#faf9f6]"
        : "bg-[#5fa292] hover:bg-[#4d8275] text-white"
    } rounded-full px-8 sm:px-10 h-auto min-h-14 py-3 text-base font-semibold whitespace-normal text-center max-w-full`}
  >
    {label}
    <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
  </Button>
);


const ReserveButton = ({ testId, light = false }) => (
  <Button
    data-testid={testId}
    onClick={scrollToInvestment}
    variant="outline"
    className={`${
      light
        ? "border-white text-white hover:bg-white/10"
        : "border-[#5fa292] text-[#5fa292] hover:bg-[#cbdad6]/40"
    } bg-transparent rounded-full px-8 sm:px-10 h-auto min-h-14 py-3 text-base font-semibold whitespace-normal text-center max-w-full`}
  >
    Reserve Now
    <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
  </Button>
);
const INCLUDES = [
  {
    title: "An in-depth initial consultation",
    body: "We start with a detailed look at your health history, symptoms, concerns, lifestyle and goals. This gives us a foundation for understanding your priorities and deciding where to focus.",
  },
  {
    title: "Personalised one-to-one consultations",
    body: "Regular sessions give us time to review progress, discuss what's changing, explore new questions and adapt your approach. The frequency will depend on your needs and preferences. Health concierge clients are prioritised for last minute or urgent consultations.",
  },
  {
    title: "An individualised health strategy",
    body: "Your recommendations will be shaped around your circumstances, preferences and goals, rather than copied from a generic programme. Depending on your needs, this may include nutrition, lifestyle, sleep, stress management and relevant hormone-health considerations.",
  },
  {
    title: "Support understanding your health information",
    body: "Have questions about relevant blood tests or other health information? We can discuss what the findings may mean, identify questions worth raising with your medical practitioner and consider what further information might be useful. Any interpretation or recommendations will remain within my professional scope, and medical diagnosis and treatment decisions belong with an appropriately qualified medical professional which I will direct you to and help you prepare for. One of the most important thing for women today is being prepared and informed when entering medical appointments so they can stand up for themselves and ask necessary questions and push for the care or testing they need, pushing back on the medical world's documented history of dismissing women's pain and discomfort.",
  },
  {
    title: "Support between consultations",
    body: "You won't have to save every question for your next appointment. You'll have access to messaging or voice message support for relevant, non-urgent questions, with clear response times and working hours.",
  },
  {
    title: "Accountability and adjustments",
    body: "Knowing what to do is one thing. Putting it into practice is another. Check-ins can help you stay focused, work through obstacles and make realistic adjustments as your needs evolve. The aim isn't to give you more things to do. It's to help you focus on what matters most.",
  },
];

const FOR_YOU = [
  "Persistent hormone-related symptoms that you want to understand more clearly.",
  "PCOS/PMOS-related concerns, including cycle irregularity and metabolic health.",
  "Acne or other skin concerns that may have hormonal or lifestyle-related contributors.",
  "Perimenopause and changing energy, cycles or wellbeing.",
  "Complex or overlapping health concerns where you need help organising your questions and thinking through your options.",
  "A frustrating cycle of researching, trying things and not knowing what to focus on next.",
  "Or want to upgrade your metabolism, longevity and overall health with a view to optimising your health for the future.",
];

export default function HealthConcierge() {
  const [currency, setCurrency] = useState(detectCurrency);
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", message: "" });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const price = PRICES[currency];

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitApplication = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/concierge/apply", form);
      setSubmitted(true);
      toast.success("Enquiry sent. Lara will be in touch personally.");
      window.open(CALENDLY_BOOKING_URL, "_blank");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-health-concierge" className="bg-[#faf9f6]">
      <Seo
        title="Hormone Health Concierge"
        description="Six months of personalised, one-to-one hormone health support. Understand your body, make informed decisions and feel confident about your health. $3,000 / £2,200."
        path="/health-concierge"
      />

      {/* HERO */}
      <section
        data-testid="concierge-hero"
        className="relative overflow-hidden"
      >
        <div
          className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#cbdad6]/40 blur-3xl pointer-events-none"
          aria-hidden
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <Overline>The Hormone Health Concierge</Overline>
                <h1 className="font-serif mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-normal text-[#333]">
                  Your Health
                  <br />
                  <span className="italic text-[#5fa292]">Deserves More.</span>
                </h1>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 text-xl md:text-2xl text-[#333] font-medium leading-relaxed max-w-xl">
                  Six months of personalised, one-to-one support to help you
                  understand your body, make informed decisions and feel more
                  confident about your hormone health.
                </p>
                <p className="mt-6 text-lg text-[#3d3d3d] font-normal leading-relaxed max-w-xl">
                  You've researched your symptoms. You've tried changing your
                  diet, bought the supplements and read the articles. Maybe
                  you've even had blood tests, only to be left wondering what
                  the results actually mean for you.
                </p>
                <p className="mt-4 text-lg text-[#3d3d3d] font-normal leading-relaxed max-w-xl">
                  You don't need another generic protocol or a list of things
                  to try. You need an approach that considers{" "}
                  <strong className="font-semibold text-[#333]">
                    you, your history, your symptoms and your life.
                  </strong>
                </p>
                <p className="mt-4 text-lg text-[#3d3d3d] font-normal leading-relaxed max-w-xl">
                  This is where the{" "}
                  <strong className="font-semibold text-[#333]">
                    Hormone Health Concierge
                  </strong>{" "}
                  service comes in: six months of continuous individualised
                  hormone-health guidance, practical support and ongoing
                  accountability, designed around your needs when you need it.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <ApplyButton testId="concierge-hero-cta" />
                  <ReserveButton testId="concierge-hero-reserve" />
                </div>
                <p
                  data-testid="concierge-price-line"
                  className="mt-6 text-sm uppercase tracking-[0.2em] text-[#5fa292]"
                >
                  Six months · One-to-one support · $3,000 / £2,400 / €2,500
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.25}>
                <div className="arch-image relative aspect-[4/5] bg-[#cbdad6]">
                  <img
                    src="/brand/health-concierge.png"
                    alt="Lara Brown working with a client online"
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 md:py-28 bg-[#f3eee8]/60 border-y border-[#e5e0d8]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight font-normal text-[#333]">
              I've been there - you shouldn't have to become{" "}
              <span className="italic text-[#5fa292]">
                your own health researcher.
              </span>
            </h2>
            <div className="mt-8 space-y-5 text-lg text-[#3d3d3d] font-normal leading-relaxed">
              <p>Getting answers about your health can feel like a job in itself.</p>
              <p>
                One practitioner focuses on one set of symptoms. Another
                recommends a different approach. Your test results arrive
                without much explanation. Online, everyone seems to have a
                different opinion about what you should eat, which supplements
                to take and which hormones to test.
              </p>
              <p>Meanwhile, you're left trying to connect the dots.</p>
              <p>
                And when you're dealing with persistent symptoms, irregular
                cycles, acne, changes in energy or the transition towards
                menopause, the last thing you need is another overwhelming
                list of things to investigate.
              </p>
              <p className="font-semibold text-[#333]">
                Your health isn't a collection of isolated symptoms. Your
                approach to it shouldn't be either.
              </p>
              <p>
                The Hormone Health Concierge gives you a dedicated space to
                work through your concerns, understand your options and
                develop a personalised approach with ongoing support.
              </p>
              <p>
                You will have access to guidance when you need it and
                personalised support on the phone.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* YOUR SUPPORT */}
      <section className="py-20 md:py-28 max-w-3xl mx-auto px-6 md:px-12">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight font-normal text-[#333]">
            Your personal{" "}
            <span className="italic text-[#5fa292]">hormone-health support</span>
          </h2>
          <div className="mt-8 space-y-5 text-lg text-[#3d3d3d] font-normal leading-relaxed">
            <p>
              Think of it as having your own dedicated hormone-health
              practitioner in your corner for six months.
            </p>
            <p>
              Someone who gets to know your history, understands what you're
              trying to achieve and can help you navigate the questions that
              come up along the way.
            </p>
            <p>
              Together, we'll work towards a clearer understanding of your
              health and a practical plan that fits your real life.
            </p>
          </div>
        </Reveal>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-20 md:py-28 bg-[#cbdad6]/30 border-y border-[#e5e0d8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <Overline>The details</Overline>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight font-normal text-[#333] mt-4">
              What your six months includes
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {INCLUDES.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.06}>
                <div
                  data-testid={`concierge-include-${i + 1}`}
                  className="bg-white border border-[#e5e0d8] rounded-3xl p-8 md:p-10 h-full transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#5fa292]/5"
                >
                  <div className="w-10 h-10 rounded-full bg-[#5fa292] text-white flex items-center justify-center text-sm font-medium">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-serif text-2xl text-[#333] mt-5 font-normal tracking-tight">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-[#3d3d3d] font-normal leading-relaxed text-sm">
                    {item.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-14 text-center">
              <ApplyButton testId="concierge-mid-cta" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="bg-white border border-[#e5e0d8] rounded-3xl p-10 md:p-12 h-full">
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight font-normal text-[#333]">
                Who is this for?
              </h2>
              <p className="mt-6 text-[#3d3d3d] font-normal leading-relaxed">
                The Hormone Health Concierge is for women who want a personal
                trainer for their hormone health. That is a professional,
                individual support to upgrade their health. You are ready to
                invest time and money in making progress.
              </p>
              <p className="mt-4 text-[#3d3d3d] font-normal leading-relaxed">
                It may be particularly relevant if you're navigating:
              </p>
              <ul className="mt-6 space-y-3">
                {FOR_YOU.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-3 text-sm text-[#3d3d3d] font-normal leading-relaxed"
                  >
                    <Check size={16} className="text-[#5fa292] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-[#f3eee8]/70 border border-[#e5e0d8] rounded-3xl p-10 md:p-12 h-full">
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight font-normal text-[#333]">
                This probably isn't for you if&hellip;
              </h2>
              <div className="mt-6 space-y-4 text-[#3d3d3d] font-normal leading-relaxed">
                <p className="flex items-start gap-3">
                  <X size={16} className="text-[#b0726a] mt-1 shrink-0" />
                  You're looking for an instant fix, a guaranteed outcome or
                  someone to take responsibility for every aspect of your
                  healthcare.
                </p>
                <p>
                  This is a collaborative relationship. I provide personalised
                  guidance, help you make sense of relevant information and
                  support you in implementing an appropriate plan. I don't
                  replace your GP, endocrinologist, gynaecologist or other
                  medical professionals, and this isn't an emergency or
                  on-demand medical service.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY SIX MONTHS */}
      <section className="py-20 md:py-28 bg-[#f3eee8]/60 border-y border-[#e5e0d8]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight font-normal text-[#333]">
              Why six months?
            </h2>
            <div className="mt-8 space-y-5 text-lg text-[#3d3d3d] font-normal leading-relaxed">
              <p>
                Because meaningful changes in health rarely happen in a
                straight line.
              </p>
              <p>
                You might make progress quickly in one area and need more time
                with another. New questions can arise. Your priorities can
                change. And sometimes, the most useful thing is having the
                time to understand what's happening before making your next
                decision.
              </p>
              <p>
                Six months gives us room to look beyond the immediate
                symptoms, build a more complete picture and refine your
                approach as we go.
              </p>
              <p>
                It also gives you something that a single appointment rarely
                can: <strong className="font-semibold text-[#333]">continuity.</strong>
              </p>
              <p>
                You won't need to explain your entire history from scratch
                every time you want to discuss a new concern.
              </p>
              <p>We'll build on what we've already learned together.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-20 md:py-28 max-w-3xl mx-auto px-6 md:px-12">
        <Reveal>
          <div
            data-testid="concierge-testimonial"
            className="relative bg-[#5fa292] text-white rounded-[2.5rem] px-8 py-12 md:px-14 md:py-14 shadow-xl shadow-[#5fa292]/20"
          >
            <span className="font-serif absolute top-6 left-8 text-6xl text-white/30 leading-none" aria-hidden>
              "
            </span>
            <p className="relative text-base md:text-lg font-normal leading-relaxed tracking-normal">
              I'm a busy entrepreneur and the health concierge option was
              perfect for me - just as I have a personal trainer in the gym,
              Lara at Aima guided me regularly through the small changes I
              needed to make to improve my overall health. She was thorough in
              ordering relevant tests at the beginning and looked at the bigger
              picture rather than just some nagging symptoms, always aiming to
              improve my overall health and not just the small issues. I would
              recommend this service to anyone who takes their health and
              longevity seriously - Lara has the answers.
            </p>
            <p className="relative mt-6 text-sm uppercase tracking-[0.15em] text-white/85">
              - Hannah Goodman · Entrepreneur
            </p>
          </div>
        </Reveal>
      </section>

      {/* INVESTMENT */}
      <section id="investment" className="py-20 md:py-28 max-w-3xl mx-auto px-6 md:px-12 scroll-mt-28">
        <Reveal>
          <Overline>Your investment</Overline>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight font-normal text-[#333] mt-4">
            Six months of personalised
            <br />
            <span className="italic text-[#5fa292]">hormone-health support</span>
          </h2>
          <div
            data-testid="concierge-currency-toggle"
            className="mt-8 inline-flex items-center gap-1 bg-white border border-[#e5e0d8] rounded-full p-1"
          >
            {Object.keys(PRICES).map((c) => (
              <button
                key={c}
                data-testid={`currency-${c.toLowerCase()}`}
                onClick={() => setCurrency(c)}
                className={`px-5 py-2 rounded-full text-sm tracking-wide transition-colors ${
                  currency === c
                    ? "bg-[#5fa292] text-white font-medium"
                    : "text-[#3d3d3d] hover:text-[#5fa292]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <p
            data-testid="concierge-investment-price"
            className="mt-6 font-serif text-4xl md:text-5xl text-[#333] tracking-tight"
          >
            {price.symbol}{price.amount}{" "}
            <span className="text-[#5fa292]">{price.label}</span>
          </p>
          <div className="mt-8 space-y-5 text-lg text-[#3d3d3d] font-normal leading-relaxed">
            <p>
              Your investment includes your initial consultation, regular
              one-to-one sessions, personalised guidance, support between
              consultations and ongoing review throughout the six-month
              engagement.
            </p>
            <p>
              You'll know what the service includes, how to contact me and
              what to expect before we begin.
            </p>
            <p>
              There are no promises of overnight transformations or miracle
              cures. The value lies in the time, attention, expertise and
              continuity dedicated to helping you navigate your health more
              thoughtfully.
            </p>
            <p>
              Consider how much time you've already spent researching
              symptoms, purchasing products, trying different approaches and
              wondering what to do next.
            </p>
            <p className="font-semibold text-[#333]">
              What would it be worth to have a clearer direction and
              individual support over the next six months?
            </p>
          </div>
          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
            <ApplyButton testId="concierge-investment-cta" label="Enquire Now" />
            <a
              href={price.payHref}
              target="_blank"
              rel="noreferrer"
              data-testid="concierge-pay-link"
            >
              <Button
                className="bg-[#333] hover:bg-[#1f1f1f] text-white rounded-full px-8 h-auto min-h-14 py-3 whitespace-normal text-center font-semibold"
              >
                Reserve now - pay {price.symbol}{price.amount} {price.label}
              </Button>
            </a>
          </div>
          <p className="mt-4 text-sm text-[#5a5a5a] font-normal">
            Most clients prefer to enquire and talk first. If you pay in full
            now, you'll be redirected to book your first call straight away.
          </p>
        </Reveal>
      </section>

      {/* FINAL CTA */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-3xl p-12 md:p-20 text-center bg-[#5fa292] text-white relative overflow-hidden">
              <div
                className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/10 blur-2xl pointer-events-none"
                aria-hidden
              />
              <div
                className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#cbdad6]/30 blur-2xl pointer-events-none"
                aria-hidden
              />
              <h2 className="relative font-serif text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight leading-tight">
                A more personal approach
                <br />
                <span className="italic">to your health starts here.</span>
              </h2>
              <div className="relative mt-8 space-y-4 text-lg text-white/90 max-w-2xl mx-auto font-normal leading-relaxed">
                <p>You don't need to know every question to ask before you begin.</p>
                <p>
                  You just need to be ready to stop piecing everything together
                  alone.
                </p>
                <p>
                  The first step is a short application telling me a little
                  about your health concerns, what you'd like help with and
                  what you're hoping to achieve.
                </p>
                <p>
                  I'll review your application and, if it looks like we could
                  be a good fit, we'll arrange a conversation to discuss your
                  needs, how the service works and whether it's right for you.
                </p>
                <p>No pressure. Just an opportunity to explore whether this is the support you've been looking for.</p>
              </div>
              <div className="relative mt-10">
                <ApplyButton
                  testId="concierge-final-cta"
                  label="Enquire Now"
                  light
                />
              </div>
              <p className="relative mt-8 text-xs uppercase tracking-[0.25em] text-white/70">
                Aima Hormone Health Concierge · Six months of personalised, one-to-one support
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ENQUIRY FORM + RESERVE */}
      <section id="apply" className="pb-24 scroll-mt-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="bg-white border border-[#e5e0d8] rounded-3xl p-10 md:p-14">
              {submitted ? (
                <div data-testid="concierge-success" className="text-center py-6">
                  <div className="w-14 h-14 rounded-full bg-[#5fa292] text-white flex items-center justify-center mx-auto">
                    <Check size={26} />
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl tracking-tight font-normal text-[#333] mt-6">
                    Your enquiry is in.
                  </h2>
                  <p className="mt-4 text-[#3d3d3d] font-normal leading-relaxed max-w-md mx-auto">
                    Thank you - I read every enquiry personally and reply within
                    two working days. If you'd like to talk sooner, grab a
                    complimentary call below.
                  </p>
                  <a
                    href={CALENDLY_BOOKING_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block mt-8"
                    data-testid="concierge-success-book"
                  >
                    <Button className="bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full px-10 h-14 text-base font-semibold">
                      Book your free call
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                </div>
              ) : (
                <>
                  <Overline>Enquire now</Overline>
                  <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight font-normal text-[#333] mt-4">
                    The Aima Hormone Health Concierge Service is for those who are
                    ready to{" "}
                    <span className="italic text-[#5fa292]">
                      invest in giving their hormone health the attention it
                      deserves.
                    </span>
                  </h2>
                  <p className="mt-6 text-[#3d3d3d] font-normal leading-relaxed">
                    A few short questions. I review every enquiry personally
                    and reply within two working days.
                  </p>
                  <form
                    onSubmit={submitApplication}
                    data-testid="concierge-apply-form"
                    className="mt-10 space-y-6"
                  >
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="hc-name" className="text-sm text-[#3d3d3d]">Name</Label>
                        <Input
                          id="hc-name"
                          required
                          placeholder="Your full name"
                          value={form.name}
                          onChange={update("name")}
                          data-testid="concierge-form-name"
                          className="mt-2 bg-[#faf9f6]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="hc-email" className="text-sm text-[#3d3d3d]">Email</Label>
                        <Input
                          id="hc-email"
                          type="email"
                          required
                          placeholder="you@email.com"
                          value={form.email}
                          onChange={update("email")}
                          data-testid="concierge-form-email"
                          className="mt-2 bg-[#faf9f6]"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="hc-phone" className="text-sm text-[#3d3d3d]">Phone number</Label>
                        <Input
                          id="hc-phone"
                          type="tel"
                          placeholder="+1 ... / +44 ..."
                          value={form.phone}
                          onChange={update("phone")}
                          data-testid="concierge-form-phone"
                          className="mt-2 bg-[#faf9f6]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="hc-location" className="text-sm text-[#3d3d3d]">Location</Label>
                        <Input
                          id="hc-location"
                          placeholder="City, country"
                          value={form.location}
                          onChange={update("location")}
                          data-testid="concierge-form-location"
                          className="mt-2 bg-[#faf9f6]"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="hc-message" className="text-sm text-[#3d3d3d]">
                        Tell me a bit about you and why you think this is right for you
                      </Label>
                      <Textarea
                        id="hc-message"
                        required
                        rows={5}
                        placeholder="What's been going on, and what would you like to change?"
                        value={form.message}
                        onChange={update("message")}
                        data-testid="concierge-form-message"
                        className="mt-2 bg-[#faf9f6]"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={sending}
                      data-testid="concierge-form-submit"
                      className="w-full bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full h-14 text-base font-semibold"
                    >
                      {sending ? "Sending..." : "Send Enquiry Now"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <p className="text-xs text-[#5a5a5a] text-center font-normal">
                      Your details go straight to Lara's inbox and are never shared.
                    </p>
                  </form>

                  <div className="mt-10 pt-8 border-t border-[#e5e0d8] text-center">
                    <p className="text-sm text-[#3d3d3d] font-normal">
                      Ready to commit already?
                    </p>
                    <a
                      href={price.payHref}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-4"
                      data-testid="concierge-form-pay-link"
                    >
                      <Button
                        variant="outline"
                        className="border-[#5fa292] text-[#5fa292] hover:bg-[#cbdad6]/40 rounded-full px-8 h-auto min-h-12 py-3 bg-transparent whitespace-normal"
                      >
                        Reserve your place - pay {price.symbol}{price.amount} {price.label}
                      </Button>
                    </a>
                    <p className="mt-3 text-xs text-[#5a5a5a] font-normal">
                      After payment you'll be redirected to book your first call.
                    </p>
                  </div>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
